import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Plus,
  Search,
  Edit,
  Trash2,
  Wallet,
  Eye,
  UserPlus,
  Pencil,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Sorting
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Quick edit balance
  const [editingBalanceId, setEditingBalanceId] = useState(null);
  const [editBalanceValue, setEditBalanceValue] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    address: '',
    nif: '',
    iban: '',
    profit_percentage: 8.6,
    initial_balance: 0,
    admin_notes: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      await axios.post(`${API_URL}/admin/users`, newUser);
      toast.success('Utilizador criado com sucesso');
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        name: '',
        phone: '',
        address: '',
        nif: '',
        iban: '',
        profit_percentage: 8.6,
        initial_balance: 0,
        admin_notes: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja eliminar o utilizador ${userName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success('Utilizador eliminado com sucesso');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar utilizador');
    }
  };

  const startEditBalance = (user) => {
    setEditingBalanceId(user.id);
    setEditBalanceValue(user.balance?.toString() || '0');
  };

  const cancelEditBalance = () => {
    setEditingBalanceId(null);
    setEditBalanceValue('');
  };

  const saveBalance = async (userId) => {
    const newBalance = parseFloat(editBalanceValue);
    if (isNaN(newBalance) || newBalance < 0) {
      toast.error('Montante inválido');
      return;
    }

    const user = users.find(u => u.id === userId);
    if (!user) return;

    const difference = newBalance - (user.balance || 0);
    if (difference === 0) {
      cancelEditBalance();
      return;
    }

    setSavingBalance(true);
    try {
      await axios.post(`${API_URL}/admin/wallet/adjust`, {
        user_id: userId,
        amount: difference,
        description: `Ajuste manual de saldo: €${user.balance?.toFixed(2)} → €${newBalance.toFixed(2)}`
      });
      toast.success('Saldo atualizado com sucesso');
      fetchUsers();
      cancelEditBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar saldo');
    } finally {
      setSavingBalance(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-slate-500" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-400" />
      : <ArrowDown className="w-4 h-4 text-blue-400" />;
  };

  // Filter, sort and pagination
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'email':
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
        break;
      case 'balance':
        aValue = a.balance || 0;
        bValue = b.balance || 0;
        break;
      case 'profit_percentage':
        aValue = a.profit_percentage || 0;
        bValue = b.profit_percentage || 0;
        break;
      case 'accumulated_returns':
        aValue = a.accumulated_returns || 0;
        bValue = b.accumulated_returns || 0;
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }
    
    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  return (
    <div className="animate-fade-in" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">Gestão de Clientes</h1>
            <p className="text-slate-400 text-sm">{users.length} utilizadores registados</p>
          </div>
        </div>

        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600 rounded-xl flex items-center gap-2" data-testid="create-user-btn">
              <UserPlus className="w-5 h-5" />
              Novo Utilizador
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#252F38] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-['Outfit']">Criar Novo Utilizador</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-300">Nome Completo *</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    required
                    placeholder="Nome do cliente"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="new-user-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    placeholder="email@exemplo.com"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="new-user-email"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Senha *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                    placeholder="Senha de acesso"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="new-user-password"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Taxa de Rendimento (%)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={newUser.profit_percentage}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setNewUser({ ...newUser, profit_percentage: value });
                      }
                    }}
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="new-user-percentage"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Saldo Inicial (€)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={newUser.initial_balance}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setNewUser({ ...newUser, initial_balance: value });
                      }
                    }}
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="new-user-balance"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Telefone</Label>
                  <Input
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    placeholder="+351 912 345 678"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">NIF (Nº Contribuinte)</Label>
                  <Input
                    value={newUser.nif}
                    onChange={(e) => setNewUser({ ...newUser, nif: e.target.value })}
                    placeholder="123456789"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">IBAN</Label>
                  <Input
                    value={newUser.iban}
                    onChange={(e) => setNewUser({ ...newUser, iban: e.target.value })}
                    placeholder="PT50..."
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Morada</Label>
                  <Input
                    value={newUser.address}
                    onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                    placeholder="Morada completa"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notas do Admin (privado)
                  </Label>
                  <Textarea
                    value={newUser.admin_notes}
                    onChange={(e) => setNewUser({ ...newUser, admin_notes: e.target.value })}
                    placeholder="Notas internas visíveis apenas para administradores..."
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl min-h-[80px]"
                    data-testid="new-user-notes"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={createLoading}
                className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl h-12 text-base"
                data-testid="submit-new-user"
              >
                {createLoading ? 'A criar...' : 'Criar Utilizador'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search, Sort and Items per page */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por nome ou email..."
            className="pl-12 h-12 bg-[#161C22] border-white/10 text-white rounded-xl"
            data-testid="search-users"
          />
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Ordenar:</span>
            <Select value={sortField} onValueChange={(v) => { setSortField(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40 bg-[#161C22] border-white/10 text-white rounded-xl" data-testid="sort-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#252F38] border-white/10">
                <SelectItem value="name">Nome (A-Z)</SelectItem>
                <SelectItem value="balance">Saldo</SelectItem>
                <SelectItem value="profit_percentage">Taxa %</SelectItem>
                <SelectItem value="accumulated_returns">Rendimentos</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="border-white/10 text-slate-300 hover:bg-white/5 px-2"
            >
              {sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Mostrar:</span>
            <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
              <SelectTrigger className="w-24 bg-[#161C22] border-white/10 text-white rounded-xl" data-testid="items-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#252F38] border-white/10">
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : paginatedUsers.length > 0 ? (
        <>
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-white/5">
                    <th 
                      className="cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Nome {getSortIcon('name')}
                      </div>
                    </th>
                    <th>Email</th>
                    <th 
                      className="cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => handleSort('balance')}
                    >
                      <div className="flex items-center gap-2">
                        Saldo {getSortIcon('balance')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => handleSort('profit_percentage')}
                    >
                      <div className="flex items-center gap-2">
                        Taxa {getSortIcon('profit_percentage')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => handleSort('accumulated_returns')}
                    >
                      <div className="flex items-center gap-2">
                        Rendimentos {getSortIcon('accumulated_returns')}
                      </div>
                    </th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="text-white font-medium">{user.name}</td>
                      <td className="text-slate-300">{user.email}</td>
                      <td>
                        {editingBalanceId === user.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={editBalanceValue}
                              onChange={(e) => {
                                const value = e.target.value.replace(',', '.');
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setEditBalanceValue(value);
                                }
                              }}
                              className="w-28 h-8 bg-[#161C22] border-white/10 text-white rounded-lg text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveBalance(user.id);
                                if (e.key === 'Escape') cancelEditBalance();
                              }}
                              data-testid={`edit-balance-input-${user.id}`}
                            />
                            <button
                              onClick={() => saveBalance(user.id)}
                              disabled={savingBalance}
                              className="p-1 hover:bg-emerald-500/20 rounded transition-colors"
                              title="Guardar"
                            >
                              <Check className="w-4 h-4 text-emerald-400" />
                            </button>
                            <button
                              onClick={cancelEditBalance}
                              className="p-1 hover:bg-red-500/20 rounded transition-colors"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span className="text-white font-semibold">{formatCurrency(user.balance)}</span>
                            <button
                              onClick={() => startEditBalance(user)}
                              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-blue-500/20 rounded transition-all"
                              title="Editar saldo"
                              data-testid={`edit-balance-btn-${user.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5 text-blue-400" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="text-blue-400">{user.profit_percentage}%</td>
                      <td className="text-slate-300">{formatCurrency(user.accumulated_returns)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4 text-slate-400" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/wallet/${user.id}`)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Gerir Wallet"
                          >
                            <Wallet className="w-4 h-4 text-emerald-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <p className="text-sm text-slate-400">
                Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredUsers.length)} de {filteredUsers.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-400" />
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                        }`}
                        data-testid={`page-${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="next-page"
                >
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {searchTerm ? 'Nenhum resultado encontrado' : 'Sem utilizadores'}
          </h3>
          <p className="text-slate-400">
            {searchTerm ? 'Tente uma pesquisa diferente.' : 'Crie o primeiro utilizador para começar.'}
          </p>
        </div>
      )}
    </div>
  );
}
